import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { IssueSeverity, ScriptPromptFrom } from '@daevio/shared';
import { REAL_USERS } from '../lib/features.ts';
import { useProductEvent } from '../lib/productEvents.ts';
import { Icon } from './ui.tsx';

const SEVERITY_RANK: Record<IssueSeverity, number> = { high: 3, medium: 2, low: 1 };

/** The page of the worst problem: the most severe, then the one most visitors hit; null without problems. */
export function worstPage(problems: { severity: IssueSeverity; hit: number; pages: string[] }[]): string | null {
  const worst = [...problems].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || b.hit - a.hit)[0];
  if (!worst) return null;
  return worst.pages[0] || '/';
}

const hiddenKey = (projectId: string) => `daevio.scriptInvite.hidden.${projectId}`;

function readHidden(projectId: string): boolean {
  try {
    return window.localStorage.getItem(hiddenKey(projectId)) === '1';
  } catch {
    return false;
  }
}

function writeHidden(projectId: string): void {
  try {
    window.localStorage.setItem(hiddenKey(projectId), '1');
  } catch {
    // Private windows: the card comes back next time, nothing else changes.
  }
}

/** The address of Sessions filtered to one page. */
export function sessionsOnPage(projectId: string, path: string): string {
  return `/p/${projectId}/sessions?path=${encodeURIComponent(path)}`;
}

/**
 * Under a finished report: from where the AI visitors got stuck to the script that shows whether real visitors do
 * too. With the script installed, straight to the real sessions on that page. Closed once, gone for the project.
 */
export function ScriptInvite({ projectId, installed, path }: { projectId: string; installed: boolean; path: string | null }) {
  const track = useProductEvent();
  const [hidden, setHidden] = useState(() => readHidden(projectId));
  const from: ScriptPromptFrom = 'report';
  const visible = REAL_USERS && !hidden;

  useEffect(() => {
    if (visible) track('script_prompt_shown', projectId, { from, installed });
  }, [visible, track, projectId, from, installed]);

  if (!visible) return null;

  const close = () => {
    writeHidden(projectId);
    setHidden(true);
  };
  const clicked = () => track('script_prompt_clicked', projectId, { from, installed });

  let title: string;
  let action: { to: string; label: string };
  if (installed) {
    title = path ? `See your real visitors on ${path}` : 'See how your real visitors do';
    action = { to: path ? sessionsOnPage(projectId, path) : `/p/${projectId}/sessions`, label: 'Watch their sessions' };
  } else {
    title = path
      ? `Your AI visitors got stuck on ${path}. Add the script to see if your real visitors do too.`
      : 'Your AI visitors made it. Add the script to see how your real visitors do.';
    action = { to: `/p/${projectId}/install`, label: 'Install script' };
  }

  return (
    <section className="r3-section" aria-label="Your real visitors">
      <div className="r3-wrap">
        <aside className="script-invite" data-testid="script-invite">
          <button className="script-invite-close" type="button" onClick={close} aria-label="Hide">
            <Icon name="close" size={14} />
          </button>
          <span className="script-invite-icon" aria-hidden="true">
            <Icon name="replay" size={20} />
          </span>
          <p className="script-invite-text">
            <strong>{title}</strong>
            {!installed && <span className="muted">One snippet on your site records real visitors next to your tests: replays, heatmaps and funnels.</span>}
          </p>
          <Link className="button primary" to={action.to} onClick={clicked}>
            {action.label}
          </Link>
        </aside>
      </div>
    </section>
  );
}
